/*
 * IWI — homepage rail behaviour (snippets/iwi-wine-rail.liquid).
 * <iwi-rail>: style chips filter the rendered cards by data-style, renumber the
 * "01 / 08" labels, show an empty note linking to the style collection, and the
 * round arrow buttons scroll the rail by roughly one viewport of cards.
 * Safe to load more than once (guarded define) and re-initialises after the theme
 * editor re-renders a section.
 */
(() => {
  if (customElements.get('iwi-rail')) return;

  const pad = (n) => String(n).padStart(2, '0');
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class IwiRail extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.track = this.querySelector('[data-iwh-track]');
      this.cards = Array.from(this.querySelectorAll('[data-iwh-card]'));
      this.chips = Array.from(this.querySelectorAll('[data-iwh-filter]'));
      this.empty = this.querySelector('[data-iwh-empty]');
      this.status = this.querySelector('[data-iwh-status]');
      this.prev = this.querySelector('[data-iwh-prev]');
      this.next = this.querySelector('[data-iwh-next]');

      this.chips.forEach((chip) =>
        chip.addEventListener('click', () => this.filter(chip), { signal })
      );
      this.prev?.addEventListener('click', () => this.page(-1), { signal });
      this.next?.addEventListener('click', () => this.page(1), { signal });
      this.track?.addEventListener('scroll', () => this.updateNav(), { passive: true, signal });
      window.addEventListener('resize', () => this.updateNav(), { passive: true, signal });
      this.updateNav();
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    filter(chip) {
      const key = chip.dataset.iwhFilter;
      this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));

      const shown = this.cards.filter((card) => key === 'all' || card.dataset.style === key);
      this.cards.forEach((card) => {
        card.hidden = !shown.includes(card);
      });
      shown.forEach((card, i) => {
        const num = card.querySelector('[data-iwh-num]');
        if (num) num.textContent = `${pad(i + 1)} / ${pad(shown.length)}`;
      });

      if (this.empty) {
        this.empty.hidden = shown.length > 0;
        const link = this.empty.querySelector('[data-iwh-empty-link]');
        if (link && chip.dataset.iwhLink) link.href = chip.dataset.iwhLink;
        this.empty.querySelectorAll('[data-iwh-empty-label]').forEach((el) => {
          el.textContent = chip.dataset.iwhLabel || 'wines';
        });
      }
      if (this.status) {
        const what = chip.dataset.iwhLabel || 'wines';
        this.status.textContent = `${shown.length} ${key === 'all' ? 'wines' : what} shown`;
      }

      if (this.track) {
        this.track.scrollTo({ left: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      this.updateNav();
    }

    page(dir) {
      if (!this.track) return;
      const step = Math.max(this.track.clientWidth * 0.85, 260);
      this.track.scrollBy({ left: dir * step, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    updateNav() {
      if (!this.track || !this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
      const nav = this.prev.parentElement;
      if (nav) nav.hidden = max <= 0 && this.track.scrollLeft <= 2;
    }
  }

  customElements.define('iwi-rail', IwiRail);
})();
